import { useMemo, useState } from "react";
import {
  Typography,
  Table,
  Space,
  Tag,
  Select,
  Input,
  Empty,
  Statistic,
  Row,
  Col,
  Card,
  Button,
  Tooltip,
  Popover,
  message,
  Drawer,
  List,
  Avatar,
  DatePicker,
  Breadcrumb,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import {
  EyeOutlined,
  FileOutlined,
  FolderFilled,
  UserOutlined,
  LockOutlined,
  ShareAltOutlined,
  LinkOutlined,
  TeamOutlined,
  DownloadOutlined,
  FolderOpenOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { SupervisedItem, ShareStatus } from "@/types";
import { useGroupItems } from "@/hooks/useSupervision";
import { fetchFileBlob } from "@/services/storageManager";
import { formatDateOnly, formatFileSize } from "@/utils/formatters";
import SupervisionPreviewModal from "./SupervisionPreviewModal";

const { Title, Text, Link } = Typography;
const { RangePicker } = DatePicker;

const DATE_PRESETS: { label: string; value: [Dayjs, Dayjs] }[] = [
  { label: "Today", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  { label: "Last 7 days", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Last 30 days", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "This month", value: [dayjs().startOf("month"), dayjs().endOf("month")] },
];

const STATUS_META: Record<ShareStatus, { color: string; label: string; icon: JSX.Element }> = {
  private: { color: "default", label: "Private", icon: <LockOutlined /> },
  shared: { color: "blue", label: "Shared", icon: <ShareAltOutlined /> },
  "public-link": { color: "orange", label: "Public link", icon: <LinkOutlined /> },
};

function StatusBadge({ item }: { item: SupervisedItem }) {
  const recipients = item.sharedWith || [];

  // Show every applicable status so uploaded-but-private is never confused with shared.
  const tags: ShareStatus[] = [];
  if (item.isShared) tags.push("shared");
  if (item.hasPublicLink) tags.push("public-link");
  if (tags.length === 0) tags.push("private");

  return (
    <Space size={4} wrap>
      {tags.map((s) => {
        const meta = STATUS_META[s];
        const tag = (
          <Tag key={s} color={meta.color} icon={meta.icon} style={s === "shared" ? { cursor: "pointer" } : undefined}>
            {meta.label}
            {s === "shared" && item.sharedWithCount > 0 ? ` (${item.sharedWithCount})` : ""}
          </Tag>
        );
        // For shared items, surface who it was shared with in a popover.
        if (s === "shared" && recipients.length > 0) {
          return (
            <Popover
              key={s}
              title="Shared with"
              content={
                <Space direction="vertical" size={2}>
                  {recipients.map((email) => (
                    <Text key={email}>
                      <UserOutlined style={{ marginRight: 6 }} />
                      {email}
                    </Text>
                  ))}
                </Space>
              }
            >
              {tag}
            </Popover>
          );
        }
        return tag;
      })}
    </Space>
  );
}

export default function SupervisionPage() {
  const { items, groups, members, memberCount, isLoading } = useGroupItems();

  const [statusFilter, setStatusFilter] = useState<ShareStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState<"all" | "file" | "folder">("all");
  const [ownerFilter, setOwnerFilter] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  const [membersOpen, setMembersOpen] = useState(false);
  const [memberSearch, setMemberSearch] = useState("");

  // Folder navigation (drill into a member's folder to see its contents).
  const [folderStack, setFolderStack] = useState<SupervisedItem[]>([]);
  const currentFolder = folderStack[folderStack.length - 1] || null;
  const openFolder = (folder: SupervisedItem) => setFolderStack([folder]);
  const enterSubfolder = (folder: SupervisedItem) => setFolderStack((s) => [...s, folder]);
  const goToCrumb = (idx: number) => setFolderStack((s) => s.slice(0, idx + 1));
  const closeFolder = () => setFolderStack([]);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [previewItem, setPreviewItem] = useState<SupervisedItem | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);

  const handlePreview = async (item: SupervisedItem) => {
    try {
      setBusyId(item.id);
      const blob = await fetchFileBlob(item.id);
      setPreviewBlob(blob);
      setPreviewItem(item);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to open preview");
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (item: SupervisedItem) => {
    try {
      setBusyId(item.id);
      const blob = await fetchFileBlob(item.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = item.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      message.success(`Downloaded ${item.name}`);
    } catch (err: any) {
      message.error(err?.response?.data?.message || "Failed to download file");
    } finally {
      setBusyId(null);
    }
  };

  const closePreview = () => {
    setPreviewItem(null);
    setPreviewBlob(null);
  };

  // Distinct owners for the member filter.
  const owners = useMemo(() => {
    const map = new Map<string, string>();
    items.forEach((i) => {
      if (i.owner?.id) map.set(i.owner.id, i.owner.name || i.owner.email || i.owner.id);
    });
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [items]);

  // Per-member document counts (total / private / shared / public link), built
  // from the items and joined onto the full member list (members with no docs
  // still appear with zeros).
  const memberRows = useMemo(() => {
    const stats = new Map<string, { total: number; priv: number; shared: number; link: number }>();
    for (const i of items) {
      const id = i.owner?.id;
      if (!id) continue;
      const s = stats.get(id) || { total: 0, priv: 0, shared: 0, link: 0 };
      s.total += 1;
      if (i.isShared) s.shared += 1;
      if (i.hasPublicLink) s.link += 1;
      if (!i.isShared && !i.hasPublicLink) s.priv += 1;
      stats.set(id, s);
    }

    const rows = members.map((m) => ({
      ...m,
      ...(stats.get(m.id) || { total: 0, priv: 0, shared: 0, link: 0 }),
    }));

    rows.sort((a, b) => b.total - a.total || (a.name || "").localeCompare(b.name || ""));
    return rows;
  }, [items, members]);

  const filteredMemberRows = useMemo(() => {
    if (!memberSearch) return memberRows;
    const q = memberSearch.toLowerCase();
    return memberRows.filter((m) => `${m.name} ${m.email}`.toLowerCase().includes(q));
  }, [memberRows, memberSearch]);

  const openMemberInTable = (memberId: string) => {
    setOwnerFilter(memberId);
    setMembersOpen(false);
  };

  // Direct contents of the folder being viewed: files in it + its subfolders.
  const folderContents = useMemo(() => {
    if (!currentFolder) return [];
    return items.filter(
      (i) =>
        (i.type === "file" && i.folderId === currentFolder.id) ||
        (i.type === "folder" && i.parentFolderId === currentFolder.id)
    );
  }, [items, currentFolder]);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      if (typeFilter !== "all" && i.type !== typeFilter) return false;
      if (ownerFilter !== "all" && i.owner?.id !== ownerFilter) return false;
      if (statusFilter !== "all") {
        if (statusFilter === "private" && (i.isShared || i.hasPublicLink)) return false;
        if (statusFilter === "shared" && !i.isShared) return false;
        if (statusFilter === "public-link" && !i.hasPublicLink) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const hay = `${i.name} ${i.owner?.name || ""} ${i.owner?.email || ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (dateRange && dateRange[0] && dateRange[1]) {
        const d = dayjs(i.createdAt);
        if (d.isBefore(dateRange[0].startOf("day")) || d.isAfter(dateRange[1].endOf("day"))) {
          return false;
        }
      }
      return true;
    });
  }, [items, ownerFilter, statusFilter, typeFilter, search, dateRange]);

  const columns: ColumnsType<SupervisedItem> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) =>
        record.type === "folder" ? (
          <Space style={{ cursor: "pointer" }} onClick={() => openFolder(record)}>
            <FolderFilled style={{ color: record.color || "#1890ff", fontSize: 18 }} />
            <Link strong>{name}</Link>
          </Space>
        ) : (
          <Space>
            <FileOutlined />
            <Text strong>{name}</Text>
          </Space>
        ),
    },
    {
      title: "Sent / uploaded by",
      dataIndex: "owner",
      key: "owner",
      width: 220,
      render: (owner) => (
        <Space>
          <UserOutlined />
          <Text>{owner?.name || owner?.email || "Unknown"}</Text>
        </Space>
      ),
    },
    {
      title: "Visibility",
      key: "status",
      width: 200,
      render: (_, record) => <StatusBadge item={record} />,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 100,
      render: (size, record) =>
        record.type === "file" && typeof size === "number" ? formatFileSize(size) : "—",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 120,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "",
      key: "actions",
      width: 130,
      render: (_, record) =>
        record.type === "file" ? (
          <Space>
            <Tooltip title="Preview">
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={busyId === record.id}
                onClick={() => handlePreview(record)}
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={busyId === record.id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          </Space>
        ) : (
          <Tooltip title="Open folder">
            <Button size="small" icon={<FolderOpenOutlined />} onClick={() => openFolder(record)}>
              Open
            </Button>
          </Tooltip>
        ),
    },
  ];

  // Columns for the folder-contents drawer (subfolders are navigable).
  const folderColumns: ColumnsType<SupervisedItem> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (name, record) =>
        record.type === "folder" ? (
          <Space style={{ cursor: "pointer" }} onClick={() => enterSubfolder(record)}>
            <FolderFilled style={{ color: record.color || "#1890ff", fontSize: 16 }} />
            <Link>{name}</Link>
          </Space>
        ) : (
          <Space>
            <FileOutlined />
            <Text>{name}</Text>
          </Space>
        ),
    },
    {
      title: "Visibility",
      key: "status",
      width: 180,
      render: (_, record) => <StatusBadge item={record} />,
    },
    {
      title: "Size",
      dataIndex: "size",
      key: "size",
      width: 90,
      render: (size, record) =>
        record.type === "file" && typeof size === "number" ? formatFileSize(size) : "—",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => formatDateOnly(date),
    },
    {
      title: "",
      key: "actions",
      width: 110,
      render: (_, record) =>
        record.type === "file" ? (
          <Space>
            <Tooltip title="Preview">
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={busyId === record.id}
                onClick={() => handlePreview(record)}
              />
            </Tooltip>
            <Tooltip title="Download">
              <Button
                size="small"
                icon={<DownloadOutlined />}
                loading={busyId === record.id}
                onClick={() => handleDownload(record)}
              />
            </Tooltip>
          </Space>
        ) : (
          <Button size="small" icon={<FolderOpenOutlined />} onClick={() => enterSubfolder(record)}>
            Open
          </Button>
        ),
    },
  ];

  return (
    <div style={{ padding: "24px 0" }}>
      <Title level={4} style={{ marginBottom: 8 }}>
        <EyeOutlined style={{ marginRight: 8 }} />
        Supervision
      </Title>
      <Text type="secondary">
        Everything uploaded and shared by the members of your assigned groups.
      </Text>

      <Row gutter={16} style={{ margin: "20px 0" }}>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title="Groups" value={groups.length} prefix={<TeamOutlined />} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card
            size="small"
            hoverable
            onClick={() => setMembersOpen(true)}
            style={{ cursor: "pointer" }}
          >
            <Statistic title="Members" value={memberCount} prefix={<UserOutlined />} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Click to view members
            </Text>
          </Card>
        </Col>
        <Col xs={12} sm={8} md={6}>
          <Card size="small">
            <Statistic title="Items" value={items.length} prefix={<FileOutlined />} />
          </Card>
        </Col>
      </Row>

      {groups.length > 0 && (
        <Space size={4} wrap style={{ marginBottom: 16 }}>
          <Text type="secondary">Groups:</Text>
          {groups.map((g) => (
            <Tag key={g.groupId} icon={<TeamOutlined />}>
              {g.displayName} ({g.memberCount})
            </Tag>
          ))}
        </Space>
      )}

      <Space wrap align="center" style={{ marginBottom: 16, width: "100%" }}>
        <Input.Search
          placeholder="Search by name or member"
          allowClear
          size="middle"
          style={{ width: 260 }}
          onChange={(e) => setSearch(e.target.value)}
        />
        <Select
          value={typeFilter}
          size="middle"
          style={{ width: 150 }}
          onChange={setTypeFilter}
          options={[
            { value: "all", label: "All types" },
            { value: "folder", label: "Folders" },
            { value: "file", label: "Documents" },
          ]}
        />
        <Select
          value={statusFilter}
          size="middle"
          style={{ width: 180 }}
          onChange={setStatusFilter}
          options={[
            { value: "all", label: "All visibility" },
            { value: "private", label: "Private" },
            { value: "shared", label: "Shared" },
            { value: "public-link", label: "Public link" },
          ]}
        />
        <Select
          value={ownerFilter}
          size="middle"
          style={{ width: 220 }}
          onChange={setOwnerFilter}
          showSearch
          optionFilterProp="label"
          options={[
            { value: "all", label: "All members" },
            ...owners.map((o) => ({ value: o.id, label: o.label })),
          ]}
        />
        <RangePicker
          value={dateRange as [Dayjs, Dayjs] | null}
          onChange={(range) => setDateRange(range as [Dayjs | null, Dayjs | null] | null)}
          presets={DATE_PRESETS}
          allowClear
          size="middle"
          style={{ width: 280 }}
        />
      </Space>

      {!isLoading && items.length === 0 ? (
        <Empty description="No items found for your groups yet" style={{ marginTop: 60 }} />
      ) : (
        <Table
          columns={columns}
          dataSource={filtered}
          rowKey="id"
          loading={isLoading}
          pagination={{ pageSize: 20, showSizeChanger: false, showTotal: (t) => `${t} items` }}
          size="small"
        />
      )}

      <SupervisionPreviewModal
        item={previewItem}
        blob={previewBlob}
        open={!!previewItem}
        onClose={closePreview}
        onDownload={handleDownload}
      />

      <Drawer
        title={
          <Breadcrumb
            items={folderStack.map((f, idx) => ({
              title:
                idx === folderStack.length - 1 ? (
                  <span>
                    <FolderOpenOutlined style={{ marginRight: 6 }} />
                    {f.name}
                  </span>
                ) : (
                  <a onClick={() => goToCrumb(idx)}>{f.name}</a>
                ),
            }))}
          />
        }
        placement="right"
        width={820}
        open={folderStack.length > 0}
        onClose={closeFolder}
      >
        {currentFolder && (
          <Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
            Owner: {currentFolder.owner?.name || currentFolder.owner?.email || "Unknown"}
          </Text>
        )}
        <Table
          columns={folderColumns}
          dataSource={folderContents}
          rowKey="id"
          pagination={false}
          size="small"
          locale={{ emptyText: "This folder is empty" }}
        />
      </Drawer>

      <Drawer
        title={`Members (${members.length})`}
        placement="right"
        width={420}
        open={membersOpen}
        onClose={() => setMembersOpen(false)}
      >
        <Input.Search
          placeholder="Search members"
          allowClear
          style={{ marginBottom: 16 }}
          onChange={(e) => setMemberSearch(e.target.value)}
        />
        <List
          dataSource={filteredMemberRows}
          locale={{ emptyText: "No members" }}
          renderItem={(m) => (
            <List.Item
              style={{ cursor: m.total > 0 ? "pointer" : "default" }}
              onClick={() => m.total > 0 && openMemberInTable(m.id)}
            >
              <List.Item.Meta
                avatar={<Avatar icon={<UserOutlined />} />}
                title={<Text strong>{m.name || m.email || m.id}</Text>}
                description={
                  <Space direction="vertical" size={4} style={{ width: "100%" }}>
                    {m.email && (
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {m.email}
                      </Text>
                    )}
                    <Space size={4} wrap>
                      <Tag icon={<FileOutlined />}>{m.total} docs</Tag>
                      <Tag color="default" icon={<LockOutlined />}>
                        {m.priv} private
                      </Tag>
                      <Tag color="blue" icon={<ShareAltOutlined />}>
                        {m.shared} shared
                      </Tag>
                      {m.link > 0 && (
                        <Tag color="orange" icon={<LinkOutlined />}>
                          {m.link} link
                        </Tag>
                      )}
                    </Space>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
      </Drawer>
    </div>
  );
}
